<script setup lang="ts">
import { Modal } from 'bootstrap'
import { dialogs } from 'frontend/_globals/dialogs'
import eventHub from 'frontend/_globals/event-hub'
import { useKeyboardShortcuts } from 'frontend/keyboard/composables/useKeyboardShortcuts'
import { KeyboardSpecialKeyTypes } from 'frontend/keyboard/enum/KeyboardSpecialKeyTypes'
import { useTheme } from 'frontend/themes/composables/useTheme'
import {
  computed,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  useSlots,
  type VNodeNormalizedChildren,
  watch,
} from 'vue'
import { useStore } from 'vuex'

const MODAL_Z_INDEX = 1060
const BACKDROP_Z_INDEX = 1059
const LAYER_Z_INDEX_STEP = 10

interface Emits {
  (e: 'closed'): void
}
const emit = defineEmits<Emits>()

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | number

interface Props {
  size?: ModalSize
  blocker?: boolean
  withoutTimeout?: boolean
  nonDraggable?: boolean
  name?: string
  scrollableContent?: boolean
  modalBodyClass?: string
  wrapperClass?: string
  hideFooter?: boolean
  beforeClose?: () => Promise<void>
  zIndex?: number
  noBodyPadding?: boolean
  hideBottomLine?: boolean
  noPaddingBottom?: boolean
  titleClass?: string
  showContentInGrid?: boolean
  fullScreenProp?: boolean
  showFullscreenButton?: boolean
  closeOnEscape?: boolean
  confirmToClose?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  size: 'lg',
  blocker: false,
  withoutTimeout: false,
  nonDraggable: false,
  name: '',
  scrollableContent: false,
  bodyClass: '',
  wrapperClass: '',
  hideFooter: false,
  beforeClose: null,
  zIndex: null,
  noBodyPadding: false,
  hideBottomLine: false,
  fullScreenProp: false,
  noPaddingBottom: false,
  titleClass: '',
  showContentInGrid: false,
  showFullscreenButton: true,
  closeOnEscape: true,
  confirmToClose: false,
})

const store = useStore()
const slots = useSlots()
const { currentThemeType } = useTheme()

useKeyboardShortcuts(
  [
    [
      [[KeyboardSpecialKeyTypes.Escape]],
      (event: KeyboardEvent) => {
        if (modalCounter.value !== currentModalStackPosition.value + 1) return

        if (props.closeOnEscape) {
          if (props.confirmToClose) {
            dialogs
              .confirm('You will lose all changes if you close this modal.', 'Are you sure?')
              .then(close)
          } else {
            close()
          }
          event.preventDefault()
          event.stopPropagation()
        }
      },
    ],
  ],
  () => {
    return true
  },
  {
    isGlobal: true,
  },
)

const moving = ref(false)
const currentModalStackPosition = ref(0)
const isModalXXl = ref(props.size == 'xxl' ? true : false)
const isModalXl = ref(props.size == 'xl' || isModalXXl.value ? true : false)
const initialSize = props.size
const mousePositionX = ref(0)
const mousePositionY = ref(0)
const modalRef = ref(null)
const modalDialogRef = ref(null)
const modal = ref(null)
const isModalFullScreen = ref(props.fullScreenProp)

let previousModalTop: number | null = null
let previousModalLeft: number | null = null

provide('modalEl', modalRef)
provide('inModal', ref(true))
provide('isModalFullScreen', isModalFullScreen)

watch(
  () => props.size,
  value => {
    isModalXXl.value = value == 'xxl'
    isModalXl.value = value == 'xl' || isModalXXl.value
  },
)

onMounted(() => {
  modal.value = new Modal(modalRef.value, { backdrop: 'static', keyboard: false })
  modal.value.show()
  setZIndexes()
  modal.value._backdrop._element.setAttribute('data-theme', currentThemeType.value)
  modalRef.value.focus() // prevent closing parent modal with esc
  modalRef.value.addEventListener('hidden.bs.modal', () => {
    store.commit('modals/close')
    emit('closed')
  })
})

const modalSizeClass = computed(() => {
  if (isModalFullScreen.value || props.fullScreenProp) {
    return 'modal-fullscreen'
  } else if (isModalXXl.value) {
    return 'modal-xxl'
  } else if (isModalXl.value) {
    return 'modal-xl'
  } else {
    return `modal-${props.size}`
  }
})

const showResizeButton = computed(() => {
  return !isModalFullScreen.value && props.size !== 'xl' && props.size !== 'xxl'
})

const scrollableContentClass = computed(() => {
  return props.scrollableContent ? 'modal-dialog-scrollable' : ''
})

const modalClasses = computed(() => {
  return [modalSizeClass.value, scrollableContentClass.value]
})

const modalZIndex = computed(() => {
  if (props.zIndex) {
    return props.zIndex
  }
  return MODAL_Z_INDEX + currentModalStackPosition.value * LAYER_Z_INDEX_STEP
})

const backdropZIndex = computed(() => {
  return BACKDROP_Z_INDEX + currentModalStackPosition.value * LAYER_Z_INDEX_STEP
})

const modalCounter = computed(() => {
  return store.getters['modals/modalCounter']
})

watch(modalCounter, value => {
  if (value === currentModalStackPosition.value + 1) {
    modalRef.value?.focus() // allows closing multiple modals with esc
  }
})

onBeforeUnmount(() => {
  modal?.value?.hide()
})

async function close() {
  if (props.beforeClose) {
    await props.beforeClose()
  }
  modal.value?.hide()
}

function resize() {
  if (initialSize === 'xxl') {
    isModalXXl.value = !isModalXXl.value
    isModalXl.value = false
  } else {
    isModalXl.value = !isModalXl.value
  }
}

function fullScreen() {
  if (isModalFullScreen.value) {
    isModalFullScreen.value = false
    modalDialogRef.value.style.left = previousModalLeft || 0
    modalDialogRef.value.style.top = previousModalTop || 0
  } else {
    previousModalLeft = modalDialogRef.value.style.left
    previousModalTop = modalDialogRef.value.style.top
    modalDialogRef.value.style.left = '0'
    modalDialogRef.value.style.top = '0'
    isModalFullScreen.value = true
  }
}

function onResizeEnd() {
  eventHub.$emit('modal-resize')
}

function mouseDown(e) {
  if (!props.nonDraggable && !isModalFullScreen.value) {
    mousePositionX.value = e.clientX
    mousePositionY.value = e.clientY
    document.body.addEventListener('mousemove', mouseMove)
  }
}

function removeMouseMoveEventListener() {
  moving.value = false
  document.body.removeEventListener('mousemove', mouseMove)
}

function mouseMove(e) {
  const mouseOffsetLeft = mousePositionX.value - e.clientX
  const mouseOffsetTop = mousePositionY.value - e.clientY
  const dialog = modalDialogRef.value

  if (e.clientY > 0 && e.clientX > 0) {
    dialog.style.top = (parseInt(dialog.style.top) || 0) - mouseOffsetTop + 'px'
    dialog.style.left = (parseInt(dialog.style.left) || 0) - mouseOffsetLeft + 'px'

    mousePositionX.value = e.clientX
    mousePositionY.value = e.clientY
  } else {
    removeMouseMoveEventListener()
  }
}

function setZIndexes() {
  let name: string | VNodeNormalizedChildren = props.name
  try {
    if (!name?.length) {
      const possibleTitle = slots.header()?.[0]?.children
      if (typeof possibleTitle === 'string') {
        name = possibleTitle
      } else if (typeof possibleTitle?.[0]?.children === 'string') {
        name = possibleTitle?.[0]?.children
      } else {
        name = `Modal ${currentModalStackPosition.value + 1}`
      }
    }
  } catch (e) {
    console.warn("Error handled, no header for modal - falling back to 'Modal - number' name", e)
    name = `Modal ${currentModalStackPosition.value + 1}`
  }

  store.commit('modals/open', {
    name,
    modalRef: {
      isModalFullScreen,
      close,
    },
  })
  currentModalStackPosition.value = modalCounter.value - 1
  modal.value._element.style.zIndex = modalZIndex.value
  modal.value._backdrop._element.style.zIndex = backdropZIndex.value
}

defineExpose({
  close,
})
</script>

<template lang="pug">
teleport(to="#modal-portal")
  .modal(ref="modalRef" @click.stop tabindex="-1")
    .modal-dialog(
      ref="modalDialogRef"
      :class="[...modalClasses, wrapperClass]"
      @transitionend="onResizeEnd"
    )
      .modal-content(:name="'modalCounter-' + currentModalStackPosition")
        ea-spinner(v-if="blocker" :spinnerSize="30" :withoutTimeout="withoutTimeout" matchParent)
        .modal-header(
          v-if="!!slots?.header"
          :class="{ 'no-padding-bottom': props.noPaddingBottom }"
          @mousedown.prevent="mouseDown"
          @mouseup.prevent="removeMouseMoveEventListener"
        )
          h5.modal-title(:class="titleClass")
            slot(:close="close" name="header")
          slot(:close="close" name="header-actions")
          .d-flex.align-items-center.modal-actions
            button.header-button(
              v-if="showResizeButton"
              @click.stop="resize()"
              @mousedown.stop
              v-tooltip.options="{ title: isModalXl ? 'Back to initial size' : 'Make wider' }"
            )
              i.mi-outlined.mi-unfold-less.transform-90deg(v-if="isModalXl")
              i.mi-outlined.mi-unfold-more.transform-90deg(v-else)
            button.header-button(
              v-if="showFullscreenButton"
              @click.stop="fullScreen()"
              @mousedown.stop
              type="button"
              v-tooltip="isModalFullScreen ? 'Make smaller' : 'Full screen'"
            )
              i.mi-outlined.mi-fullscreen-exit(v-if="isModalFullScreen")
              i.mi-outlined.mi-fullscreen(v-else)
            button.header-button(
              @click.stop="close"
              @mousedown.stop
              aria-label="Close"
              input-class="form-control-sm"
            )
              i.mi-outlined.mi-close
              //- i.fa.fa-times
        .modal-body(
          :class="[modalBodyClass, { 'no-padding': props.noBodyPadding, 'use-grid': showContentInGrid }]"
        )
          slot(:close="close" name="default")
        .bottom-line(v-if="!props.hideFooter && !props.hideBottomLine")
        .modal-footer(v-if="!props.hideFooter")
          slot(:close="close" name="footer")
            dismiss-button(@click="close()")
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.modal-body {
  z-index: 1;
  position: relative;
  border-top: 1px solid map.get(themeVariables.$colors, outlineVariant);
  padding: 24px;
  background: map.get(themeVariables.$colors, surface);

  &.no-padding {
    padding: 0 !important;
  }

  &.use-grid {
    display: grid;
  }
}

.modal-header {
  padding: 0 16px 0 24px;
  min-height: 64px;
  align-items: center;
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-areas: '. . modal-actions';
  gap: 8px;

  .mi-outlined {
    font-size: 22px;
  }

  &.no-padding-bottom {
    padding-bottom: 0;
  }
}

.modal-actions {
  grid-area: modal-actions;
}

.header-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: map.get(themeVariables.$colors, onSurfaceVariant);
  cursor: pointer;
  border: none;
  background: transparent;

  &:hover {
    color: map.get(themeVariables.$colors, onSurface);
  }
}

.modal-footer {
  padding: 8px 16px !important;
  height: auto !important;
}

.bottom-line {
  border-bottom: 1px solid map.get(themeVariables.$colors, outlineVariant);
  width: 100%;
  height: 1px;
  padding: 0;
}
</style>
